'use client';

import { useGetWidgetQuery } from '@akinon/next/data/client/misc';
import { useLocalization } from '@akinon/next/hooks';
import { GoogleMap, LoadScriptNext, Marker } from '@react-google-maps/api';
import { Icon, Link, LoaderSpinner } from '@theme/components';
import CustomPin from '@theme/assets/icons/yatsan-pin.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';

function Map({ lat, lng }) {
  const [map, setMap] = useState(null);
  const center = useMemo(
    () => ({
      lat: parseFloat(lat),
      lng: parseFloat(lng)
    }),
    [lat, lng]
  );

  const onMapLoad = useCallback((map) => {
    setMap(map);
  }, []);

  useEffect(() => {
    if (map) {
      window.google.maps.event.trigger(map, 'resize');
      map.setCenter(center);
    }
  }, [map, center]);

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={center}
      zoom={17}
      onLoad={onMapLoad}
    >
      <Marker
        options={{
          icon: {
            url: CustomPin.src
          }
        }}
        position={center}
      />
    </GoogleMap>
  );
}

export default function ContactMaps() {
  const { status, data, isLoading } = useGetWidgetQuery('contact-info-widget');
  const { data: mapData } = useGetWidgetQuery('acc-stores-page');
  const { t } = useLocalization();

  if (isLoading || status !== 'fulfilled' || !data) {
    return <LoaderSpinner />;
  }

  const mapApiKey = mapData?.attributes?.map_api_key?.value;

  if (!mapApiKey) {
    // Or some other placeholder/error message
    return <div>Map cannot be loaded.</div>;
  }

  return (
    <LoadScriptNext googleMapsApiKey={mapApiKey}>
      <div className="flex flex-col items-start gap-10">
        <div className="flex flex-col gap-10 lg:gap-[56px] items-start max-w-[400px] mt-10">
          <div className="flex flex-col items-start gap-5">
            <h2 className="text-sm font-medium">
              {t('common.contact_us.customer_support')}
            </h2>
            <div className="flex items-start flex-col gap-4">
              {data?.attributes?.contact_info?.value
                ?.customer_support_phone && (
                <Link
                  href={`tel:${data?.attributes?.contact_info?.value?.customer_support_phone}`}
                  className="text-xl font-medium text-gray hover:text-primary-black duration-300 transition-colors"
                >
                  {
                    data?.attributes?.contact_info?.value
                      ?.customer_support_phone
                  }
                </Link>
              )}
              <div className="flex items-baseline gap-1">
                {data?.attributes?.contact_info?.value
                  ?.working_hours_weekdays && (
                  <div className="text-xs">
                    <span className="text-primary-black ">{`${t(
                      'common.contact_us.weekdays'
                    )}: `}</span>
                    <span className="text-gray">
                      {
                        data?.attributes?.contact_info?.value
                          ?.working_hours_weekdays
                      }
                    </span>
                  </div>
                )}
                {data?.attributes?.contact_info?.value
                  ?.working_hours_weekend && (
                  <div className="text-xs">
                    {' / '}
                    <span className="text-primary-black ">{`${t(
                      'common.contact_us.weekend'
                    )}: `}</span>
                    <span className="text-gray">
                      {
                        data?.attributes?.contact_info?.value
                          ?.working_hours_weekend
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {data?.attributes?.central_management?.map((item, index) => {
            return (
              <div className="flex flex-col items-start gap-5" key={index}>
                {item?.value?.name && (
                  <h2 className="text-sm font-medium">{item?.value?.name}</h2>
                )}
                <div className="flex flex-col items-start text-sm">
                  {item?.value?.address && (
                    <p className=" text-gray">{item?.value?.address}</p>
                  )}
                  {item?.value?.phone_number && (
                    <span className="text-primary-black">
                      {t('common.contact_us.phone_number')}
                      {': '}
                      <Link
                        href={`tel:${item?.value?.phone_number}`}
                        className=" text-gray hover:text-primary-black duration-300 transition-colors"
                      >
                        {item?.value?.phone_number}
                      </Link>
                    </span>
                  )}
                  {item?.value?.fax_number && (
                    <span className="text-primary-black">
                      {t('common.contact_us.fax_number')}
                      {': '}
                      <Link
                        href={`tel:${item?.value?.fax_number}`}
                        className=" text-gray hover:text-primary-black duration-300 transition-colors"
                      >
                        {item?.value?.fax_number}
                      </Link>
                    </span>
                  )}
                  <div className="flex items-baseline gap-1">
                    {item?.value?.working_hours_weekdays && (
                      <div className="text-xs">
                        <span className="text-primary-black ">{`${t(
                          'common.contact_us.weekdays'
                        )}: `}</span>
                        <span className="text-gray">
                          {item?.value?.working_hours_weekdays}
                        </span>
                      </div>
                    )}
                    {item?.value?.working_hours_weekend && (
                      <div className="text-xs">
                        {' / '}
                        <span className="text-primary-black ">{`${t(
                          'common.contact_us.weekend'
                        )}: `}</span>
                        <span className="text-gray">
                          {item?.value?.working_hours_weekend}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {data?.attributes?.central_management?.map((item, index) => {
          if (!item?.value?.latitude && !item?.value?.longitude) {
            return null;
          }
          return (
            <div key={index} className="w-full h-[400px] relative">
              <div className="absolute top-2 left-2 z-[1] bg-white px-4 py-5 rounded-[4px] max-w-[206px] flex flex-col items-start gap-2">
                {item?.value?.name && (
                  <h3 className="text-xl font-medium">{item?.value?.name}</h3>
                )}
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${item?.value?.latitude},${item?.value?.longitude}`}
                  target="_blank"
                  className="flex items-center"
                >
                  <span className="text-sm text-primary-black font-medium underline hover:text-primary-blue duration-300 transition-colors ">
                    {t('common.contact_us.see_on_map')}
                  </span>
                  <Icon name="chevron-right" size={24} className="-rotate-45" />
                </Link>
              </div>
              {item?.value?.latitude && item?.value?.longitude && (
                <Map lat={item?.value?.latitude} lng={item?.value?.longitude} />
              )}
            </div>
          );
        })}
      </div>
    </LoadScriptNext>
  );
}
